import { useQuery } from '@tanstack/react-query';
import { useHybridAuth } from './useHybridAuth';
import { QueryConfig } from '../api/client';
import type { Paginated } from '@relay-towing/api';
import { driverApi, DriverCall } from '../services/driverApi';
import { useUpdateCallStatus } from './useUpdateCallStatus';
import { getStoredSelectedCompanyId } from './userCompanies.shared';
import { useEffect, useRef } from 'react';

const getDriverCalls = async (
  token: string,
  companyId: string
): Promise<Paginated<DriverCall>> => {
  driverApi.setAuthToken(token);
  return driverApi.getCalls(companyId);
};

export type UseDriverCallsOptions = {
  /**
   * When false, skips the board request (GET /calls?assignedToMe=true).
   * Use false while profile is loading or when the API returns placeholder profile id "0" (no Driver row for selected company).
   */
  queryEnabled?: boolean;
  /**
   * Company the calls belong to, from the driver profile. The request waits
   * until it is known: the endpoint rejects a call without company context,
   * and on a fresh login the stored selection the interceptor reads is not
   * there yet.
   */
  companyId?: string;
};

export const useDriverCalls = (
  config?: QueryConfig<typeof getDriverCalls>,
  options?: UseDriverCallsOptions
) => {
  const { getAccessTokenSilently, isAuthenticated } = useHybridAuth();
  const updateCallStatusMutation = useUpdateCallStatus();
  const queryEnabled = options?.queryEnabled !== false;
  const boardCompanyId =
    options?.companyId || getStoredSelectedCompanyId() || undefined;

  // Use a ref to store the last check time to avoid unnecessary updates
  const lastCheckRef = useRef<number>(0);

  const result = useQuery({
    ...config,
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      return getDriverCalls(token, boardCompanyId as string);
    },
    // The board is one company's: the profile's, or the stored selection
    // while the profile has none (an owner with no Driver row there). The key
    // names it, so a switch never shows the other company's calls from the
    // cache, and the request waits for it: the endpoint refuses a call
    // without company context.
    queryKey: ['driver-calls', boardCompanyId],
    enabled: isAuthenticated && queryEnabled && !!boardCompanyId,
    staleTime: 30 * 1000, // 30 seconds for calls data
    refetchInterval: 30 * 1000, // Refetch calls every 30 seconds for real-time updates
    refetchIntervalInBackground: true, // Keep refetching in background
    refetchOnWindowFocus: true, // Refetch when window regains focus
  });

  // Check for past scheduled calls and update their status
  useEffect(() => {
    const checkPastScheduledCalls = async () => {
      // Only proceed if we have data and it's been at least 15 seconds since the last check
      const now = Date.now();
      if (!result.data?.data || now - lastCheckRef.current < 15000) return;

      // Update the last check time
      lastCheckRef.current = now;

      console.log('🔄 Checking for past scheduled calls...', {
        callsCount: result.data.data.length,
        currentTime: new Date().toISOString(),
      });

      const currentTime = new Date();
      const pendingCallsToUpdate = result.data.data.filter((call) => {
        // Check if the call is PENDING and has a scheduledAt time in the past
        return (
          call.status === 'PENDING' &&
          call.scheduledAt &&
          new Date(call.scheduledAt) <= currentTime
        );
      });

      if (pendingCallsToUpdate.length > 0) {
        console.log(
          `Found ${pendingCallsToUpdate.length} pending calls that are past their scheduled time:`,
          pendingCallsToUpdate.map((c) => ({
            id: c.id,
            scheduledAt: c.scheduledAt,
          }))
        );

        // Update each call's status to NEW
        for (const call of pendingCallsToUpdate) {
          try {
            console.log(
              `Updating call ${call.id} status from PENDING to NEW (scheduled time: ${call.scheduledAt})`
            );
            await updateCallStatusMutation.mutateAsync({
              callId: call.id,
              status: 'NEW',
            });
          } catch (error) {
            console.error(`Failed to update call ${call.id} status:`, error);
          }
        }
      } else {
        console.log('No pending calls past their scheduled time found');
      }
    };

    // Run the check when data changes
    checkPastScheduledCalls();
  }, [result.data, updateCallStatusMutation]);

  return result;
};
